import { useState, useEffect } from "react";
import { MessageCircle, X } from "lucide-react";

/**
 * Floating WhatsApp button — appare dopo scroll con animazione pulse.
 * Apre chat WhatsApp Business con messaggio pre-compilato.
 */
const WHATSAPP_NUMBER = "390818380897"; // Numero business Colucci Assicuratori (+39 081 8380897)
const PRE_MESSAGES = {
  default: "Salve, vorrei informazioni su una polizza assicurativa.",
  auto: "Salve, vorrei un preventivo per RC Auto.",
  casa: "Salve, vorrei un preventivo per la mia casa.",
  noleggio: "Salve, sono interessato a un'auto a noleggio lungo termine.",
  viaggio: "Salve, vorrei un preventivo per assicurazione viaggio.",
  consulenza: "Salve, vorrei prenotare una consulenza gratuita.",
};

export default function WhatsAppFloat({ context = "default" }) {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const d = sessionStorage.getItem("wa-dismissed");
    if (d) { setDismissed(true); return; }
    const onScroll = () => setVisible(window.scrollY > 400);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!visible || expanded) return;
    const t = setTimeout(() => setExpanded(true), 2500);
    const t2 = setTimeout(() => setExpanded(false), 7500);
    return () => { clearTimeout(t); clearTimeout(t2); };
  }, [visible, expanded]);

  if (dismissed || !visible) return null;

  const msg = encodeURIComponent(PRE_MESSAGES[context] || PRE_MESSAGES.default);
  const waUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${msg}`;

  return (
    <div className="fixed bottom-6 right-6 z-50 flex items-end gap-3" data-testid="wa-float">
      {expanded && (
        <div
          className="hidden sm:block bg-white rounded-2xl shadow-2xl border border-zinc-200 p-4 max-w-[260px] animate-fade-slide-in"
          style={{ animation: "fadeSlideIn 0.4s ease-out" }}
          data-testid="wa-bubble">
          <div className="flex items-start justify-between gap-2 mb-1">
            <p className="text-xs font-bold text-[#0F1F14]">Ciao! 👋</p>
            <button onClick={() => { setDismissed(true); sessionStorage.setItem("wa-dismissed", "1"); }}
              className="text-zinc-400 hover:text-zinc-700" aria-label="Chiudi">
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <p className="text-xs text-zinc-600 leading-snug">
            Hai domande? Scrivi in chat: rispondiamo entro <strong>15 min</strong> negli orari d&apos;ufficio.
          </p>
        </div>
      )}

      <a
        href={waUrl}
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Scrivi su WhatsApp"
        className="group relative w-14 h-14 rounded-full bg-[#25D366] shadow-xl shadow-[#25D366]/30 flex items-center justify-center hover:scale-105 transition-transform"
        data-testid="wa-button"
        onClick={() => { try { window.gtag?.("event", "whatsapp_click", { context }); } catch {} }}>
        <span className="absolute inset-0 rounded-full bg-[#25D366] animate-ping opacity-30" />
        <MessageCircle className="relative w-6 h-6 text-white" strokeWidth={2.5} />
        <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-red-500 border-2 border-white" />
      </a>
      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateX(10px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </div>
  );
}
